import { useTranslator } from '@bluebeam/react-i18n';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import Markup from 'editor/components/Markup';
import RFIPost from 'editor/components/RFIPost';
import Immutable from 'immutable';
import React, { FC, useContext } from 'react';
import { PostRFI } from 'records/interactions/PostRFI';
import { ViewRFI } from 'records/interactions/ViewRFI';

type PostRFIInteractionContentProps = {
  interaction: PostRFI;
};

const PostRFIInteractionContent: FC<PostRFIInteractionContentProps> = (
  props
) => {
  const { interaction } = props;
  const { actions } = useContext(AppStoreContext);
  const { translate } = useTranslator(['buttons', 'mapView']);
  const world = useContext(ECSWorldContext);
  // Make this an event passed in via a prop
  const clearRFIPostComps = () => {
    world.locateAll(RFIPost).forEach((e) => e.destroy());
  };

  // Make this an event passed in via a prop
  const showHiddenMarkups = () => {
    world.grabAll(Markup).forEach(({ component }) => {
      component.hidden = false;
    });
  };

  const handlePostRFICancelClick = async (): Promise<void> => {
    // if drawn or not?
    if (interaction.markup !== null) {
      const markupComp = world.grabBy(
        Markup,
        (comp) => comp.markup.markupid === interaction.markup?.markupid
      )?.component;

      if (markupComp) {
        markupComp.selected = false; // poor man's way to remove the Remove button.
      }

      if (!interaction.updatingMarkup) {
        actions.removeLocalMarkup(interaction.markup);
      } else if (interaction.originalMarkup) {
        await actions.removeLocalMarkup(interaction.markup);
        await actions.setLocalMarkup(interaction.originalMarkup);
      }
    }

    if (interaction.removingMarkup && interaction.originalMarkup) {
      // recreating the markup, because it is deleted from ECSSheetMarkup - handleRemoveClick func
      await actions.createMarkup(interaction.originalMarkup);
    }

    showHiddenMarkups();
    actions.clearInteractions();
    clearRFIPostComps();
  };

  const handlePostRFIConfirmClick = async (): Promise<void> => {
    const { markup, originalMarkup, rfi } = interaction;

    // just exit state because it was removed
    if (!markup) {
      showHiddenMarkups();
      await actions.clearInteractions();
      clearRFIPostComps();

      return;
    }

    // updated markup
    const markupEntity = world.find(
      (entity) =>
        entity.has(Markup) &&
        entity.get(Markup).markup.markupid === markup.markupid
    );

    if (markupEntity) {
      const markupComp = markupEntity.get(Markup);
      const updatedMarkup = markup.set(
        'jsonBlob',
        JSON.stringify(markupComp.mkMarkup?.getAsObject())
      );

      // moving/resizing only
      if (originalMarkup) {
        await actions.clearInteractions(); // force this to be empty on update
        await actions.updateMarkup(updatedMarkup);
      } else {
        // brand new markup (either deleted original or 1st draw) (delete the temp before posting)
        await actions.removeLocalMarkup(markup);
        await actions.postMarkupsToRFI(rfi, Immutable.List([updatedMarkup]));
      }
    }

    showHiddenMarkups();
    await actions.clearInteractions();
    clearRFIPostComps();

    world.grabAll(Markup).forEach(({ component }) => {
      component.selected = markup.markupid === component.markup.markupid;
    });

    actions.createInteraction(
      new ViewRFI({
        backArrow: true,
        containerStyles: { height: 'calc(100vh - 96px)', bottom: '48px' },
        markup,
        placing: true,
        rfi,
      })
    );
  };
  return (
    <>
      <div
        className="pos-a flex-center"
        style={{ zIndex: 3, top: '128px', left: 'calc(50vw - 157.5px)' }}
      >
        <div
          className="bgd-black-1-opacity-80 white-1 px4 py3 flex-center b-radius-3"
          style={{ width: '315px' }}
        >
          {interaction.updatingMarkup
            ? translate('mapView:postRFIMessage.moveOrResizeMarkup') // FIXME TODO: complete translate call
            : translate('mapView:postRFIMessage.placeRFIMarkup')}
        </div>
      </div>
      <div
        className="pos-a hrem-4p5 w100 bgd-white-2 flex justify-end items-center px4"
        style={{ position: 'sticky', bottom: '0' }}
      >
        <button className="btn btn-link" onClick={handlePostRFICancelClick}>
          {translate('buttons:cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={
            !interaction.updatingMarkup &&
            interaction.markup === null &&
            !interaction.removingMarkup
          }
          onClick={handlePostRFIConfirmClick}
        >
          {interaction.removingMarkup
            ? translate('buttons:saveChanges')
            : translate('buttons:confirmPlacement')}
        </button>
      </div>
    </>
  );
};

export default PostRFIInteractionContent;
